import { createStore } from 'jotai/vanilla';
import React, { useRef, type PropsWithChildren } from 'react';
import type { SidePanelSession } from '../types';
import { SidePanelSystemContext, type SidePanelCommandHandler } from './contexts/SidePanelSystemContext';
import { createSerialCommandQueue } from './domainLogic/createSerialCommandQueue';
import { layoutAtom, sessionAtom } from './state/atoms';

export type SidePanelSystemProviderProps = PropsWithChildren<{
    initialSession?: SidePanelSession;
    commandHandlers?: Record<string, SidePanelCommandHandler>;
    onSessionClose?: () => void;
}>;

type SidePanelSystemProviderState = {
    store: ReturnType<typeof createStore>;
    commandHandlers: Map<string, Map<string, SidePanelCommandHandler>>;
    onSessionClose?: () => void;
    controlQueue: ReturnType<typeof createSerialCommandQueue>;
};

export function SidePanelSystemProvider({
    children,
    initialSession,
    commandHandlers: initialHandlers,
    onSessionClose
}: SidePanelSystemProviderProps) {
    const contextValueRef = useRef<SidePanelSystemProviderState | null>(null);

    // Create one isolated store, handler registry, and command queue per provider; seed legacy inputs only once.
    if (!contextValueRef.current) {
        const store = createStore();
        if (initialSession) {
            store.set(sessionAtom, initialSession);
            store.set(layoutAtom, { isExpanded: false });
        }
        const ownerHandlers = new Map<string, Map<string, SidePanelCommandHandler>>();
        if (initialSession && initialHandlers) {
            ownerHandlers.set(initialSession.ownerId, new Map(Object.entries(initialHandlers)));
        }
        contextValueRef.current = {
            store,
            commandHandlers: ownerHandlers,
            onSessionClose,
            controlQueue: createSerialCommandQueue()
        };
    }

    return (
        <SidePanelSystemContext.Provider value={contextValueRef.current}>{children}</SidePanelSystemContext.Provider>
    );
}
